import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/feature/PageMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const articles = getArticlesByCategory(slug || '');

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20 px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center py-20">
              <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
                <i className="ri-emotion-sad-line text-2xl text-foreground-500"></i>
              </div>
              <h1 className="text-xl font-semibold text-foreground-950 mb-2">分类未找到</h1>
              <p className="text-sm text-foreground-600 mb-6">请检查链接是否正确</p>
              <Link to="/" className="inline-flex px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 奶水乐园`,
    description: category.description,
    url: `https://naishuileyuan.com/category/${category.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: articles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://naishuileyuan.com/article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://naishuileyuan.com' },
      { '@type': 'ListItem', position: 2, name: category.name, item: `https://naishuileyuan.com/category/${category.slug}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <PageMeta
        title={`${category.name} - 奶水乐园`}
        description={category.description}
        keywords={`${category.name},母乳喂养,奶水乐园,${category.name}方法,${category.name}技巧`}
        canonicalUrl={`https://naishuileyuan.com/category/${category.slug}`}
      />
      <SchemaOrg data={categorySchema} />
      <SchemaOrg data={breadcrumbSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />

            {/* Category Header */}
            <div className="py-8 md:py-12">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className={`${category.icon} text-primary-600 text-xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                    {category.name}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1">{articles.length} 篇文章</p>
                </div>
              </div>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed max-w-3xl">
                {category.description}
              </p>
            </div>

            {/* Category Switcher */}
            <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-8 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                    cat.slug === category.slug
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/60'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Article List */}
            {articles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 pb-16">
                {articles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-foreground-500">{article.readTime} 分钟阅读</span>
                        <span className="text-xs text-foreground-400">·</span>
                        <span className="text-xs text-foreground-500">{article.publishDate}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-file-list-3-line text-2xl text-foreground-500"></i>
                </div>
                <p className="text-sm text-foreground-600">该分类暂无文章</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}